import { useCallback, useEffect, useRef, useState } from 'react';
import { useAtomValue } from 'jotai';
import { cloneProdService } from '@/services/clone-prod.service';
import { selectedAccountIdAtom } from '@/hooks/use-account';
import { queryClient } from '@/lib/query-client';
import type { CloneProdDto, CloneProdJob } from '@/types';

const POLL_MS = 3000;

/**
 * Start a production clone into the selected account and follow it to the end.
 *
 * The copy takes a few minutes, so machine answers with a job and this hook polls it. The
 * business-unit list is refreshed at the start (a new brand may have been created) and again
 * when the job settles.
 */
export const useCloneProd = () => {
  const accountId = useAtomValue(selectedAccountIdAtom);
  const [job, setJob] = useState<CloneProdJob | null>(null);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const stop = () => {
    if (timer.current) clearTimeout(timer.current);
    timer.current = null;
  };

  useEffect(() => stop, []);

  const refresh = useCallback(
    () => queryClient.invalidateQueries({ queryKey: ['business-units', accountId] }),
    [accountId]
  );

  const poll = useCallback(
    (jobId: string) => {
      if (accountId == null) return;
      timer.current = setTimeout(async () => {
        try {
          const next = await cloneProdService.job(accountId, jobId);
          setJob(next);
          if (next.status === 'running') poll(jobId);
          else await refresh();
        } catch {
          // A missed poll is not a failed clone: the job keeps running in kim-ai-backend.
          poll(jobId);
        }
      }, POLL_MS);
    },
    [accountId, refresh]
  );

  const start = async (payload: CloneProdDto) => {
    if (accountId == null) throw new Error('No account selected');
    stop();
    const { job: started } = await cloneProdService.start(accountId, payload);
    setJob(started);
    await refresh();
    poll(started.jobId);
    return started;
  };

  return { job, start, reset: () => { stop(); setJob(null); } };
};
